import {
  useElementBounding,
  useIdle,
  useLocalStorage,
  useMouse,
  usePreferredReducedMotion,
  useRafFn,
} from "@vueuse/core";
import type { Ref } from "vue";
import { useConsultantKpiApi } from "~/components/consultant-kpi/composables/useConsultantKpiApi";
import {
  MASCOT_ANIMATION_DURATIONS_MS,
  type MascotAnimation,
  type MascotEvolution,
  mascotEvolutionFromStages,
  mascotLookFromPointer,
  mascotSpriteState,
  mascotStageFromFullTimeEquivalent,
  mascotStageFromStorage,
} from "~/components/mascot/launcher/mascot-launcher.model";
import { type MascotStage, mascotSpriteBounds } from "~/components/mascot/launcher/mascot-sprite.model";
import { useUserMe } from "~/components/user/composables/useUser";
import { useGleap } from "~/composables/useGleap";

const SLEEP_AFTER_INACTIVITY_MS = 5 * 60 * 1000;

export const useMascotLauncher = (launcherElement: Ref<HTMLElement | null>) => {
  const { teamMember, isDeveloper } = useUserMe();
  const { getConsultantKpi } = useConsultantKpiApi();
  const { open, unreadCount } = useGleap();
  const { idle } = useIdle(SLEEP_AFTER_INACTIVITY_MS);
  const reducedMotion = usePreferredReducedMotion();
  const { x: mouseX } = useMouse({ type: "client" });
  const launcherBounding = useElementBounding(launcherElement);
  const lastSeenStage = useLocalStorage("mascot-launcher-last-stage", 0);

  const stage = ref<MascotStage>(1);
  const animation = ref<MascotAnimation>("idle");
  const evolution = ref<MascotEvolution | null>(null);
  const animationStartedAtMs = ref(0);
  const nowMs = ref(performance.now());
  let animationTimeout: ReturnType<typeof setTimeout> | undefined;

  const isResting = computed(() => animation.value === "idle" || animation.value === "sleep");
  const look = computed(() =>
    mascotLookFromPointer({
      pointerX: mouseX.value,
      launcherLeft: launcherBounding.left.value,
      launcherWidth: launcherBounding.width.value,
    })
  );
  const spriteState = computed(() =>
    mascotSpriteState({
      animation: animation.value,
      stage: stage.value,
      evolution: evolution.value,
      elapsedMs: nowMs.value - animationStartedAtMs.value,
      nowMs: nowMs.value,
      look: look.value,
      reducedMotion: reducedMotion.value === "reduce",
    })
  );
  const spriteBounds = computed(() => mascotSpriteBounds(stage.value));

  useRafFn(
    () => {
      nowMs.value = performance.now();
    },
    { fpsLimit: 12 }
  );

  const play = (nextAnimation: keyof typeof MASCOT_ANIMATION_DURATIONS_MS) => {
    clearTimeout(animationTimeout);
    animation.value = nextAnimation;
    animationStartedAtMs.value = performance.now();
    animationTimeout = setTimeout(() => {
      if (evolution.value) stage.value = evolution.value.toStage;
      evolution.value = null;
      animation.value = idle.value ? "sleep" : "idle";
    }, MASCOT_ANIMATION_DURATIONS_MS[nextAnimation]);
  };

  const showStage = (nextStage: MascotStage, nextEvolution: MascotEvolution | null) => {
    if (nextEvolution) {
      evolution.value = nextEvolution;
      play("evolve");
      return;
    }
    // A pending evolution would land on its own stage when its timeout fires, over this one.
    if (evolution.value) {
      clearTimeout(animationTimeout);
      evolution.value = null;
      animation.value = "idle";
    }
    stage.value = nextStage;
  };

  const loadStage = async (teamMemberId: string) => {
    try {
      const kpi = await getConsultantKpi({ teamMemberId, period: "week", referenceDate: new Date() });
      const nextStage = mascotStageFromFullTimeEquivalent(kpi.planning.fullTimeEquivalent);
      const nextEvolution = mascotEvolutionFromStages(mascotStageFromStorage(lastSeenStage.value), nextStage);
      lastSeenStage.value = nextStage;
      showStage(nextStage, nextEvolution);
    } catch {
      // The launcher must keep opening Gleap without the consultant KPI: it stays at the first stage.
    }
  };

  watch(
    () => teamMember.value?.id,
    (teamMemberId) => {
      if (teamMemberId) loadStage(teamMemberId);
    },
    { immediate: true }
  );

  watch(unreadCount, (count, previousCount) => {
    if (count > previousCount) play("notify");
  });

  watch(idle, (isIdle) => {
    if (!isResting.value) return;
    if (isIdle) animation.value = "sleep";
    else play("greet");
  });

  onBeforeUnmount(() => clearTimeout(animationTimeout));

  // Developers try every stage and its evolution without changing their planning.
  const previewStage = (nextStage: MascotStage) =>
    showStage(nextStage, mascotEvolutionFromStages(stage.value, nextStage));

  const onClick = () => {
    open();
    play("laugh");
  };

  const onHover = () => {
    if (animation.value === "idle") play("greet");
  };

  return {
    animation,
    spriteState,
    spriteBounds,
    unreadCount,
    stage,
    canPreviewStages: isDeveloper,
    previewStage,
    onClick,
    onHover,
  };
};
